import { useCallback, useEffect, useMemo, useState } from "react";
import { cdName } from "../lib/filename";
import { http, formatApiError } from "../lib/api";
import { vacationCall } from "../lib/compliance";
import { toast } from "sonner";
import { AgentSearch } from "../components/AgentSearch";
import { useAuth } from "../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { useRealtime } from "../context/RealtimeContext";
import { AbsChip } from "../components/absences/common";
import { CopyVacationsDialog } from "../components/planning/CopyVacationsDialog";
import { NonBillableDialog, NonBillableField } from "../components/planning/NonBillable";
import { ReplacementPicks } from "../components/absences/CpAndReplacements";
import { ChevronLeft, ChevronRight, Plus, X, Trash2, GripVertical, Building2, MapPin, FileDown, FileText, CalendarRange, ShieldAlert, AlertTriangle, Eye, Ban, Copy, ReceiptText } from "lucide-react";
import { ACTIVITIES, useActivities, siteActivities } from "../lib/activities";
import { NewActivityButton } from "../components/NewActivity";
import { PlanningReceiptsDialog } from "../components/PlanningReceipts";
import { BroadcastButton } from "../components/BroadcastMessage";
import { DateInput } from "../components/DateInput";
import { sortForSite } from "../components/PreferredAgents";
import { SearchSelect } from "../components/SearchSelect";

const frHolidays = (y) => {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4, g = Math.floor((8 * b + 13) / 25);
  const h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 19 * l) / 433);
  const mo = Math.floor((h + l - 7 * m + 90) / 25), easter = Date.UTC(y, mo - 1, (h + l - 7 * m + 33 * mo + 19) % 32);
  const iso = (t) => new Date(t).toISOString().slice(0, 10);
  return [...["01-01", "05-01", "05-08", "07-14", "08-15", "11-01", "11-11", "12-25"].map((x) => `${y}-${x}`), ...[1, 39, 50].map((n) => iso(easter + n * 864e5))];
};
const FR_HOLIDAYS = new Set(Array.from({ length: 15 }, (_, n) => 2024 + n).flatMap(frHolidays));

function startOfWeek(d) {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - dow);
  x.setHours(0,0,0,0);
  return x;
}
function startOfMonth(d) { const x = new Date(d.getFullYear(), d.getMonth(), 1); x.setHours(0,0,0,0); return x; }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate()+n); return x; }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function fmt(d) {
  // Format YYYY-MM-DD en HEURE LOCALE (toISOString = UTC → décalage en France)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function shiftClass(type) {
  return {
    regular: "shift-regular", night: "shift-night", holiday: "shift-holiday",
    sunday: "shift-sunday", unassigned: "shift-unassigned",
  }[type] || "shift-regular";
}

// Sélecteur mois + année pour sauter directement à une date sans multi-clics
function MonthYearPicker({ cursor, onPick }) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(cursor.getFullYear());
  const currentMonth = cursor.getMonth();
  const currentYear = cursor.getFullYear();
  const label = cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const months = [
    "Janvier","Février","Mars","Avril","Mai","Juin",
    "Juillet","Août","Septembre","Octobre","Novembre","Décembre",
  ];
  // Ferme le popover quand on clique en dehors
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!e.target.closest?.("[data-month-year-picker]")) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  useEffect(() => { if (open) setYear(cursor.getFullYear()); }, [open, cursor]);

  return (
    <div className="relative" data-month-year-picker>
      <button
        type="button"
        data-testid="month-year-picker"
        title="Aller directement à un mois"
        onClick={() => setOpen((v) => !v)}
        className="px-3 py-2 text-sm font-semibold rounded-md border border-slate-300 hover:bg-slate-50 capitalize inline-flex items-center gap-2"
      >
        <CalendarRange className="h-4 w-4 text-slate-500" />
        {label}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 z-50 bg-white border border-slate-200 rounded-md shadow-lg p-3 w-64" data-testid="picker-popover">
          <div className="flex items-center justify-between mb-2">
            <button type="button" data-testid="picker-year-prev" onClick={() => setYear((y) => y - 1)} className="p-1 rounded hover:bg-slate-100"><ChevronLeft className="h-4 w-4"/></button>
            <div className="font-display text-lg font-extrabold">{year}</div>
            <button type="button" data-testid="picker-year-next" onClick={() => setYear((y) => y + 1)} className="p-1 rounded hover:bg-slate-100"><ChevronRight className="h-4 w-4"/></button>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {months.map((m, idx) => {
              const isCurrent = year === currentYear && idx === currentMonth;
              return (
                <button
                  key={idx}
                  type="button"
                  data-testid={`picker-month-${idx}`}
                  onClick={() => { onPick(year, idx); setOpen(false); }}
                  className={`px-2 py-2 text-xs font-semibold rounded-md border transition-colors ${isCurrent ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                >
                  {m.slice(0, 4)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Planning() {
  const [view, setView] = useState("week"); // 'week' | 'month' | 'day' | 'agents'
  const [params, setParams] = useSearchParams();
  const [cursor, setCursor] = useState(() => { const d = params.get("date"); return d ? new Date(`${d}T12:00:00`) : startOfWeek(new Date()); });
  const [receiptsOpen, setReceiptsOpen] = useState(false);
  const [vacations, setVacations] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [filterClient, setFilterClient] = useState("");
  const [filterSite, setFilterSite] = useState("");
  const [copy, setCopy] = useState(null);
  const [nbOpen, setNbOpen] = useState(false);
  const authCtx = useAuth();
  const canNb = authCtx.user?.role === "admin" || Array.isArray(authCtx.user?.site_ids);
  const [exporting, setExporting] = useState(false);
  const [genDevisOpen, setGenDevisOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const { lastEvent } = useRealtime();
  useEffect(() => {
    const vid = params.get("vacation");
    const hit = vid && vacations.find((x) => x.id === vid);
    if (!hit) return;
    setEditing(hit);
    params.delete("vacation"); params.delete("date"); setParams(params, { replace: true });
  }, [vacations, params, setParams]);

  const range = useMemo(() => {
    if (view === "week" || view === "agents") {
      const start = startOfWeek(cursor);
      return { start, end: addDays(start, 6), days: Array.from({length:7}, (_,i)=>addDays(start,i)) };
    }
    if (view === "day") {
      const d = new Date(cursor); d.setHours(0,0,0,0);
      return { start: d, end: d, days: [d] };
    }
    // month
    const mStart = startOfMonth(cursor);
    const mEnd = endOfMonth(cursor);
    const gridStart = startOfWeek(mStart);
    const gridEnd = addDays(startOfWeek(mEnd), 6);
    const days = [];
    for (let d = new Date(gridStart); d <= gridEnd; d = addDays(d, 1)) days.push(new Date(d));
    return { start: gridStart, end: gridEnd, days, mStart, mEnd };
  }, [view, cursor]);

  const rangeStartKey = fmt(range.start);
  const rangeEndKey = fmt(range.end);
  const autoAssign = async () => {
    const site = filterSite ? sites.find((x) => x.id === filterSite) : null;
    if (!window.confirm(`Affecter automatiquement les vacations non affectées du ${fmt(range.days[0])} au ${fmt(range.days[range.days.length - 1])}${site ? ` sur ${site.name}` : ""} ? Les agents préconisés du site passent en premier.`)) return;
    try {
      const { data } = await http.post("/vacations/auto-assign", { start: fmt(range.days[0]), end: fmt(range.days[range.days.length - 1]), site_id: filterSite || null });
      toast.success(`${data.assigned} vacation(s) affectée(s) dont ${data.preferred} à des agents préconisés · ${data.remaining} restante(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const load = useCallback(async () => {
    const [v, a, s, c, cv, cs] = await Promise.all([
      http.get(`/vacations?start=${rangeStartKey}&end=${rangeEndKey}`),
      http.get("/agents"), http.get("/sites"), http.get("/clients"),
      http.get(`/compliance/violations?start=${rangeStartKey}&end=${rangeEndKey}`).catch(() => ({ data: [] })),
      http.get("/compliance/closed-sites").catch(() => ({ data: {} })),
    ]);
    const flags = {};
    cv.data.forEach((x) => x.vacation_ids.forEach((id) => { (flags[id] ||= []).push(`${x.label} : ${x.detail}`); }));
    setVacations(v.data.map((x) => ({ ...x, _violations: flags[x.id] || [] }))); setAgents(a.data); setSites(s.data.map((x) => ({ ...x, _closed: cs.data[x.id] || null }))); setClients(c.data);
  }, [rangeStartKey, rangeEndKey]);
  useEffect(() => { load(); }, [load]);
  const openCopy = (mode, base) => {
    const src = mode === "day" ? (byDay[base] || []) : range.days.flatMap((d) => byDay[fmt(d)] || []);
    if (!src.length) { toast.info(mode === "day" ? "Aucune vacation à copier ce jour-là pour ce site" : "Aucune vacation à copier cette semaine pour ce site"); return; }
    setCopy({ mode, base, sources: [...src].sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time)) });
  };
  const [absences, setAbsences] = useState([]);
  const loadAbs = useCallback(() => http.get("/absences", { params: { start: rangeStartKey, end: rangeEndKey, status: "approved", ...(filterSite ? { site_id: filterSite } : {}) } })
    .then((r) => setAbsences(r.data)).catch((e) => { setAbsences([]); toast.error(`Absences non chargées : ${formatApiError(e)}`); }), [rangeStartKey, rangeEndKey, filterSite]);
  useEffect(() => { loadAbs(); }, [loadAbs]);
  useEffect(() => { if (["absence_changed", "vacation_changed"].includes(lastEvent?.event)) loadAbs(); }, [lastEvent, loadAbs]);
  const absByDay = useMemo(() => {
    const m = {};
    absences.filter((a) => PLANNING_ABS.includes(a.short)).forEach((a) => a.days.forEach((d) => (m[d] ||= []).push(a)));
    return m;
  }, [absences]);
  useEffect(() => {
    if (!lastEvent) return;
    if (["vacation_changed","agent_changed","site_changed","client_changed","compliance_changed"].includes(lastEvent.event)) load();
  }, [lastEvent, load]);

  // Sites disponibles pour le filtre Site (selon le client choisi)
  const filteredSites = useMemo(() => {
    if (!filterClient) return sites;
    return sites.filter((s) => s.client_id === filterClient);
  }, [sites, filterClient]);

  // Si on change de client, on remet le filtre site à "tous" si l'actuel n'appartient plus au client
  useEffect(() => {
    if (filterClient && filterSite) {
      const stillThere = filteredSites.some((s) => s.id === filterSite);
      if (!stillThere) setFilterSite("");
    }
  }, [filterClient, filterSite, filteredSites]);

  // Filtrer les vacations selon site/client
  const visibleVacations = useMemo(() => {
    if (filterSite) return vacations.filter((v) => v.site_id === filterSite);
    if (filterClient) {
      const ids = new Set(filteredSites.map((s) => s.id));
      return vacations.filter((v) => ids.has(v.site_id));
    }
    return vacations;
  }, [vacations, filterSite, filterClient, filteredSites]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      let start, end, periodLabel;
      if (view === "month") {
        const mStart = startOfMonth(cursor);
        const mEnd = endOfMonth(cursor);
        start = fmt(mStart); end = fmt(mEnd);
        periodLabel = cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
      } else {
        start = fmt(range.start); end = fmt(range.end);
        periodLabel = view === "day"
          ? range.days[0].toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
          : `Du ${range.start.toLocaleDateString("fr-FR")} au ${range.end.toLocaleDateString("fr-FR")}`;
      }
      const params = new URLSearchParams({ start, end, period_label: periodLabel });
      if (filterSite) params.set("site_id", filterSite);
      else if (filterClient) params.set("client_id", filterClient);

      const r = await http.get(`/planning/export-pdf?${params.toString()}`, { responseType: "blob" });

      // Si le serveur retourne un blob d'erreur (ex. JSON 400), le décoder
      const ct = r.headers?.["content-type"] || r.data?.type || "";
      if (ct.includes("application/json") || (r.data?.size && r.data.size < 200 && !ct.includes("pdf"))) {
        const text = await r.data.text();
        try { const j = JSON.parse(text); throw new Error(j.detail || "Erreur export PDF"); }
        catch (parseErr) { throw new Error(text || "Erreur export PDF"); }
      }

      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const labelPart = filterSite
        ? sites.find((s) => s.id === filterSite)?.name?.replace(/\s+/g, "_") || "site"
        : filterClient
          ? clients.find((c) => c.id === filterClient)?.name?.replace(/\s+/g, "_") || "client"
          : "tous-sites";
      const filename = `planning_${labelPart}_${start}_${end}.pdf`;

      // Téléchargement classique
      const a = document.createElement("a");
      a.href = url; a.download = cdName(r.headers?.["content-disposition"], filename);
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      // Fallback : si le téléchargement n'a pas été déclenché (iframe restrictif), ouvrir dans un nouvel onglet
      setTimeout(() => {
        try { a.remove(); } catch (e) { /* noop */ }
        URL.revokeObjectURL(url);
      }, 1500);
    } catch (e) {
      const msg = e?.response ? formatApiError(e) : (e?.message || String(e));
      alert("Échec export PDF : " + msg);
    } finally {
      setExporting(false);
    }
  };

  const byDay = useMemo(() => {
    const map = {};
    for (const d of range.days) map[fmt(d)] = [];
    for (const v of visibleVacations) if (map[v.date]) map[v.date].push(v);
    for (const k of Object.keys(map)) map[k].sort((a, b) => a.start_time.localeCompare(b.start_time));
    return map;
  }, [range.days, visibleVacations]);

  const navigate = (dir) => {
    if (view === "week" || view === "agents") setCursor(addDays(cursor, 7 * dir));
    else if (view === "day") setCursor(addDays(cursor, dir));
    else setCursor(addMonths(cursor, dir));
  };
  const today = () => {
    if (view === "week" || view === "agents") setCursor(startOfWeek(new Date()));
    else if (view === "day") { const d = new Date(); d.setHours(0,0,0,0); setCursor(d); }
    else setCursor(startOfMonth(new Date()));
  };

  const openCreate = (date, agentId) => {
    // Pré-remplir le site selon le filtre actif (filterSite > premier site du client > premier site)
    const preselected = filterSite
      || (filterClient ? filteredSites[0]?.id : null)
      || sites[0]?.id
      || "";
    setEditing({ date, site_id: preselected, agent_id: agentId || "", start_time: "08:00", end_time: "16:00", activity: "", notes: "" });
  };

  const dropError = (err) => {
    if (err.cancelled) return;
    if (err.requested) { toast.success(err.message); return; }
    alert(formatApiError(err));
  };
  const handleDragStart = (e, v) => { setDragging(v); e.dataTransfer.effectAllowed = "move"; };
  const handleDragOver = (e, key) => { e.preventDefault(); setDragOver(key); };

  // For day/week/month views: target is a date string
  const handleDrop = async (e, newDate) => {
    e.preventDefault(); setDragOver(null);
    if (!dragging || dragging.date === newDate) { setDragging(null); return; }
    try {
      await vacationCall("put", `/vacations/${dragging.id}`, {
        site_id: dragging.site_id, agent_id: dragging.agent_id,
        date: newDate, start_time: dragging.start_time, end_time: dragging.end_time,
        shift_type: dragging.shift_type, activity: dragging.activity || null, notes: dragging.notes || "", min_floor: dragging.min_floor !== false,
      });
      load();
    } catch (err) { dropError(err); }
    finally { setDragging(null); }
  };

  // For agents view: target is "agentId|date" — assign to that agent on that date
  const handleDropOnAgent = async (e, agentId, newDate) => {
    e.preventDefault(); setDragOver(null);
    if (!dragging) return;
    const sameSlot = dragging.agent_id === agentId && dragging.date === newDate;
    if (sameSlot) { setDragging(null); return; }
    try {
      await vacationCall("put", `/vacations/${dragging.id}`, {
        site_id: dragging.site_id, agent_id: agentId || null,
        date: newDate, start_time: dragging.start_time, end_time: dragging.end_time,
        shift_type: dragging.shift_type, activity: dragging.activity || null, notes: dragging.notes || "", min_floor: dragging.min_floor !== false,
      });
      load();
    } catch (err) { dropError(err); }
    finally { setDragging(null); }
  };

  const title = useMemo(() => {
    if (view === "week" || view === "agents") return `Semaine du ${range.days[0].toLocaleDateString("fr-FR")} au ${range.days[6].toLocaleDateString("fr-FR")}`;
    if (view === "day") return range.days[0].toLocaleDateString("fr-FR", { weekday:"long", day:"2-digit", month:"long", year:"numeric" });
    return cursor.toLocaleDateString("fr-FR", { month:"long", year:"numeric" });
  }, [view, range.days, cursor]);

  return (
    <div className="p-6 max-w-[1800px] mx-auto" data-testid="planning-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Planning</h1>
          <p className="text-sm text-slate-600 capitalize">{title}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" data-testid="view-toggle">
            {[["day","Jour"],["week","Semaine"],["month","Mois"],["agents","Par agent"]].map(([k, l]) => (
              <button key={k} data-testid={`view-${k}`} onClick={()=>setView(k)} className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest ${view===k?"bg-slate-900 text-white":"bg-white text-slate-700 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
          <button type="button" title="Période précédente" data-testid="prev" onClick={()=>navigate(-1)} className="p-2 rounded-md border border-slate-300 hover:bg-slate-50"><ChevronLeft className="h-4 w-4"/></button>
          <MonthYearPicker cursor={cursor} onPick={(y, m) => setCursor(new Date(y, m, 1))} />
          <button type="button" title="Aujourd'hui" data-testid="today" onClick={today} className="px-3 py-2 text-sm font-semibold rounded-md border border-slate-300 hover:bg-slate-50">Aujourd&apos;hui</button>
          <button type="button" title="Période suivante" data-testid="next" onClick={()=>navigate(1)} className="p-2 rounded-md border border-slate-300 hover:bg-slate-50"><ChevronRight className="h-4 w-4"/></button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 text-[11px] font-bold uppercase tracking-widest">
        <span className="shift-regular px-2 py-1 rounded">Standard</span>
        <span className="shift-night px-2 py-1 rounded">Nuit</span>
        <span className="shift-holiday px-2 py-1 rounded">Férié</span>
        <span className="shift-sunday px-2 py-1 rounded">Dimanche</span>
        <span className="shift-unassigned px-2 py-1 rounded">Non affecté</span>
        <span className="ml-3 text-slate-500 inline-flex items-center gap-1"><GripVertical className="h-3 w-3"/>Glissez une vacation pour la déplacer</span>
      </div>

      {/* Barre de filtres : Client + Site + Export PDF */}
      <div className="flex flex-wrap items-end gap-3 mb-4 p-3 border border-slate-200 rounded-md bg-slate-50/40">
        <div className="flex flex-col gap-1 min-w-[220px]">
          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><Building2 className="h-3 w-3"/>Client</label>
          <SearchSelect
            data-testid="filter-client"
            value={filterClient}
            onChange={(e) => setFilterClient(e.target.value)}
            className="border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          >
            <option value="">Tous les clients</option>
            {clients.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </SearchSelect>
        </div>
        <div className="flex flex-col gap-1 min-w-[260px]">
          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><MapPin className="h-3 w-3"/>Site</label>
          <SearchSelect
            data-testid="filter-site"
            value={filterSite}
            onChange={(e) => setFilterSite(e.target.value)}
            className="border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          >
            <option value="">{filterClient ? `Tous les sites (${filteredSites.length})` : `Tous les sites`}</option>
            {filteredSites.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
          </SearchSelect>
        </div>
        {(filterClient || filterSite) && (
          <button
            data-testid="filter-clear"
            onClick={() => { setFilterClient(""); setFilterSite(""); }}
            className="text-xs font-semibold text-slate-600 hover:text-slate-900 underline mb-1.5"
          >
            Réinitialiser
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-500">{visibleVacations.length} vacation{visibleVacations.length > 1 ? "s" : ""} affichée{visibleVacations.length > 1 ? "s" : ""}</span>
          <button
            data-testid="auto-assign-btn"
            onClick={autoAssign}
            className="inline-flex items-center gap-1.5 bg-[#0B2A7A] hover:bg-[#061A55] text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-md transition-colors"
            title="Affecter automatiquement les vacations non affectées de la période (agents préconisés en priorité)"
          >
            Remplissage auto
          </button>
          <button
            data-testid="generate-devis"
            onClick={() => setGenDevisOpen(true)}
            className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-md transition-colors"
            title="Générer un devis à partir des vacations affichées"
          >
            <FileText className="h-3.5 w-3.5" />
            Générer devis
          </button>
          {canNb && <button data-testid="nb-bulk-btn" disabled={!filterSite} onClick={() => setNbOpen(true)} title={filterSite ? "Rendre des vacations non facturables (ou de nouveau facturables)" : "Choisissez d'abord un site dans le filtre"}
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-slate-800 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-md transition-colors">
            <ReceiptText className="h-3.5 w-3.5" />Non facturable</button>}
          {(view === "week" || view === "day") && (
            <button data-testid="copy-period-btn" disabled={!filterSite} onClick={() => openCopy(view === "week" ? "week" : "day", fmt(range.days[0]))}
              title={filterSite ? (view === "week" ? "Copier la semaine vers les semaines suivantes du mois" : "Copier la journée vers d'autres jours") : "Choisissez d'abord un site dans le filtre"}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-slate-800 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-md transition-colors">
              <Copy className="h-3.5 w-3.5" />{view === "week" ? "Copier la semaine" : "Copier la journée"}
            </button>
          )}
          <BroadcastButton initial={() => ({ site_id: filterSite, start: fmt(range.start), end: fmt(range.end) })}
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-md transition-colors" />
          <button
            data-testid="open-read-receipts"
            onClick={() => setReceiptsOpen(true)}
            className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-md transition-colors"
            title="Voir quels agents ont consulté leur planning du mois"
          >
            <Eye className="h-3.5 w-3.5" />
            Accusés de lecture
          </button>
          {receiptsOpen && <PlanningReceiptsDialog initialMonth={`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`} onClose={() => setReceiptsOpen(false)} />}
          <button
            data-testid="export-pdf"
            onClick={exportPdf}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-md transition-colors"
            title={view === "month" ? "Exporter le planning du mois en PDF" : "Exporter le planning de la période en PDF"}
          >
            <FileDown className="h-3.5 w-3.5" />
            {exporting ? "Export…" : `Exporter PDF (${view === "month" ? "mois" : view === "day" ? "jour" : "semaine"})`}
          </button>
        </div>
      </div>

      {nbOpen && <NonBillableDialog siteId={filterSite} siteName={sites.find((x) => x.id === filterSite)?.name || ""} defaultStart={fmt(range.days[0])} defaultEnd={fmt(range.days[range.days.length - 1])} onClose={() => setNbOpen(false)} onDone={load} />}
      {copy && <CopyVacationsDialog {...copy} viewMonth={`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`} siteId={filterSite} siteName={sites.find((x) => x.id === filterSite)?.name || ""} onClose={() => setCopy(null)} onDone={load} />}
      {view === "week" && <WeekView onCopyDay={filterSite ? (k) => openCopy("day", k) : null} absByDay={absByDay} range={range} byDay={byDay} agents={agents} sites={sites} onCreate={openCreate} onEdit={setEditing} onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop} dragOver={dragOver}/>}
      {view === "day" && <DayView abs={absByDay[fmt(range.days[0])] || []} date={range.days[0]} items={byDay[fmt(range.days[0])] || []} agents={agents} sites={sites} onCreate={openCreate} onEdit={setEditing} onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop} dragOver={dragOver}/>}
      {view === "month" && <MonthView absByDay={absByDay} range={range} cursor={cursor} byDay={byDay} sites={sites} agents={agents} onCreate={openCreate} onEdit={setEditing} onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop} dragOver={dragOver}/>}
      {view === "agents" && <AgentsView absences={absences} days={range.days} vacations={visibleVacations} agents={agents} sites={sites} onCreate={openCreate} onEdit={setEditing} onDragStart={handleDragStart} onDragOver={handleDragOver} onDropOnAgent={handleDropOnAgent} dragOver={dragOver}/>}

      {editing && <VacationModal value={editing} onClose={()=>setEditing(null)} onSaved={load} agents={agents} sites={sites}/>}
      {genDevisOpen && (
        <GenerateDevisModal
          range={range}
          view={view}
          cursor={cursor}
          clients={clients}
          sites={sites}
          filterClient={filterClient}
          filterSite={filterSite}
          visibleCount={visibleVacations.length}
          onClose={() => setGenDevisOpen(false)}
          onCreated={() => {
            setGenDevisOpen(false);
            window.location.assign("/devis");
          }}
        />
      )}
    </div>
  );
}

function labelDay(d) { return d.toLocaleDateString("fr-FR", { weekday:"short", day:"2-digit", month:"short" }); }

function VacationCard({ v, site, agent, compact=false, onDragStart, onClick }) {
  return (
    <button
      draggable
      onDragStart={(e)=>onDragStart(e, v)}
      onClick={onClick}
      data-testid={`vacation-${v.id}`}
      className={`${!agent && v.subcontractor_id ? "bg-amber-50 border-l-4 border-[#C9962E] text-slate-900" : shiftClass(v.shift_type)} w-full text-left rounded-md px-2 py-1.5 transition-all hover:-translate-y-0.5 hover:shadow-sm cursor-grab active:cursor-grabbing`}
    >
      <div className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
        {v.start_time} – {v.end_time}
        {v.non_billable && <span data-testid={`vacation-nb-${v.id}`} title={`Non facturable au client — ${v.non_billable_reason || ""}`} className="shrink-0 rounded px-1 text-[9px] font-extrabold bg-slate-800 text-white">NF</span>}
        {(v.site_banned || v.ban_override) && (
          <span data-testid={`vacation-site-banned-${v.id}`} className="ml-auto shrink-0" title={v.ban_override ? `Affectation forcée par ${v.ban_override.by} — ${v.ban_override.reason}` : "Agent interdit sur ce site : vacation à réaffecter"}><Ban className="h-3.5 w-3.5 text-rose-600" /></span>
        )}
        {(v._violations?.length > 0 || v.compliance_override) && (
          <span data-testid={`vacation-violation-${v.id}`} className="ml-auto shrink-0" title={[...(v._violations || []), v.compliance_override ? `Dérogation validée par ${v.compliance_override.by}${v.compliance_override.reason ? ` — ${v.compliance_override.reason}` : ""}` : ""].filter(Boolean).join("\n")}><ShieldAlert className="h-3.5 w-3.5 text-rose-600" /></span>
        )}
      </div>
      {!compact && <div className="text-xs font-semibold truncate mt-0.5 flex items-center gap-1">
        {site?._closed && <span data-testid={`vacation-closed-client-${v.id}`} title={`Client fermé dans l'annuaire : ${site._closed.client_name}`}><Building2 className="h-3 w-3 text-rose-600 shrink-0" /></span>}
        <span className="truncate">{site?.name || "Site ?"}</span></div>}
      <div className="text-[11px] opacity-80 truncate">{agent ? `${agent.first_name[0]}. ${agent.last_name}` : v.subcontractor_id ? "Sous-traitant (à désigner)" : "Non affecté"}</div>
      {v.activity && (
        <div className="inline-flex items-center mt-1 px-1.5 py-[1px] rounded text-[9px] font-bold uppercase tracking-wider bg-white/60 backdrop-blur-sm border border-white/30">
          {ACTIVITIES.find((a) => a.code === v.activity)?.label || v.activity}
        </div>
      )}
    </button>
  );
}

const PLANNING_ABS = ["CP", "M", "CSS", "AI"];
const AbsLine = ({ list, k }) => !list?.length ? null : (
  <div className="flex flex-wrap gap-1" data-testid={`planning-abs-${k}`}>{list.map((a) => <AbsChip key={a.id} a={a} testid={`planning-abs-${k}-${a.agent_id}`} />)}</div>
);

function MonthDatesPicker({ base, value, onChange, perDay }) {
  if (!base) return <p className="mt-3 text-xs text-slate-500">Choisissez d'abord la date de la vacation.</p>;
  const [y, m] = base.split("-").map(Number);
  const n = new Date(y, m, 0).getDate();
  const lead = (new Date(y, m - 1, 1).getDay() + 6) % 7;
  const iso = (d) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const toggle = (d) => onChange(value.includes(d) ? value.filter((x) => x !== d) : [...value, d].sort());
  const total = (value.length + 1) * perDay;
  return (
    <div className="mt-3 space-y-2" data-testid="month-dates-picker">
      <div className="text-[11px] text-slate-500">Cochez les autres jours de {new Date(y, m - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })} (copies créées à pourvoir).</div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase text-slate-400">{["L", "M", "M", "J", "V", "S", "D"].map((l, i) => <div key={i}>{l}</div>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: lead }, (_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: n }, (_, i) => {
          const d = iso(i + 1), isBase = d === base, on = value.includes(d), hol = FR_HOLIDAYS.has(d), sun = (lead + i) % 7 === 6;
          return (
            <button key={d} type="button" disabled={isBase} onClick={() => toggle(d)} data-testid={`pick-date-${d}`}
              title={isBase ? "Date de la vacation" : hol ? "Jour férié" : ""}
              className={`h-8 rounded-md text-xs font-bold border transition-colors ${isBase ? "bg-slate-900 text-white border-slate-900" : on ? "bg-sky-600 text-white border-sky-600" : `bg-white border-slate-200 hover:border-sky-400 ${hol || sun ? "text-rose-600" : "text-slate-700"}`}`}>{i + 1}</button>
          );
        })}
      </div>
      <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest">
        <button type="button" onClick={() => onChange([])} className="text-sky-700 hover:underline" data-testid="pick-dates-clear">Tout décocher</button>
        <span className="ml-auto text-[11px] normal-case tracking-normal font-semibold text-sky-800" data-testid="pick-dates-summary">{value.length + 1} jour{value.length ? "s" : ""} × {perDay} = <b>{total} vacation{total > 1 ? "s" : ""}</b></span>
      </div>
    </div>
  );
}

function WeekView({ onCopyDay, absByDay, range, byDay, agents, sites, onCreate, onEdit, onDragStart, onDragOver, onDrop, dragOver }) {
  return (
    <div className="grid-planning border border-slate-200 rounded-md overflow-hidden bg-white">
      {range.days.map((d) => {
        const key = fmt(d);
        const isSunday = d.getDay() === 0;
        const isHoliday = FR_HOLIDAYS.has(key);
        const isToday = fmt(new Date()) === key;
        const isDragOver = dragOver === key;
        return (
          <div key={key}
            onDragOver={(e)=>onDragOver(e, key)} onDrop={(e)=>onDrop(e, key)}
            className={`border-r last:border-r-0 border-slate-200 min-h-[460px] transition-colors ${isDragOver?"bg-sky-50":isHoliday?"bg-amber-50":isSunday?"bg-indigo-50":""}`}>
            <div className={`flex items-center justify-between px-3 py-2 border-b border-slate-200 ${isToday ? "bg-sky-600 text-white" : "bg-slate-50"}`}>
              <div className="text-xs font-bold uppercase tracking-widest">{labelDay(d)}</div>
              <div className="flex items-center gap-0.5">
                {onCopyDay && byDay[key]?.length > 0 && <button data-testid={`copy-day-${key}`} title="Copier vers d'autres jours" onClick={() => onCopyDay(key)} className={`rounded-md p-1 ${isToday ? "hover:bg-sky-700" : "hover:bg-slate-200"}`}><Copy className="h-3.5 w-3.5"/></button>}
                <button data-testid={`add-vacation-${key}`} onClick={() => onCreate(key)} className={`rounded-md p-1 ${isToday ? "hover:bg-sky-700" : "hover:bg-slate-200"}`}><Plus className="h-4 w-4"/></button>
              </div>
            </div>
            <div className="p-2 space-y-2">
              <AbsLine list={absByDay[key]} k={key} />
              {byDay[key]?.map((v) => (
                <VacationCard key={v.id} v={v} site={sites.find(s=>s.id===v.site_id)} agent={agents.find(a=>a.id===v.agent_id)} onDragStart={onDragStart} onClick={()=>onEdit({...v})}/>
              ))}
              {byDay[key]?.length === 0 && <button onClick={()=>onCreate(key)} className="w-full border-2 border-dashed border-slate-200 rounded-md text-xs py-4 text-slate-400 hover:border-sky-400 hover:text-sky-600">+ Ajouter</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayView({ abs, date, items, agents, sites, onCreate, onEdit, onDragStart, onDragOver, onDrop, dragOver }) {
  const key = fmt(date);
  const isSunday = date.getDay() === 0;
  const isHoliday = FR_HOLIDAYS.has(key);
  return (
    <div onDragOver={(e)=>onDragOver(e, key)} onDrop={(e)=>onDrop(e, key)}
      className={`border border-slate-200 rounded-md bg-white min-h-[500px] ${dragOver===key?"bg-sky-50":isHoliday?"bg-amber-50":isSunday?"bg-indigo-50":""}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div className="text-sm font-bold capitalize">{date.toLocaleDateString("fr-FR", { weekday:"long", day:"2-digit", month:"long" })}</div>
        <button data-testid={`add-vacation-${key}`} onClick={()=>onCreate(key)} className="inline-flex items-center gap-1 text-xs font-semibold bg-sky-600 text-white rounded-md px-3 py-1 hover:bg-sky-700"><Plus className="h-3.5 w-3.5"/> Nouvelle vacation</button>
      </div>
      {abs.length > 0 && <div className="px-4 pt-3"><AbsLine list={abs} k={key} /></div>}
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((v) => (
          <div key={v.id} className="relative">
            <VacationCard v={v} site={sites.find(s=>s.id===v.site_id)} agent={agents.find(a=>a.id===v.agent_id)} onDragStart={onDragStart} onClick={()=>onEdit({...v})}/>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center text-slate-400 py-12 text-sm border-2 border-dashed border-slate-200 rounded-md">Aucune vacation ce jour.</div>}
      </div>
    </div>
  );
}

function MonthView({ absByDay, range, cursor, byDay, sites, agents, onCreate, onEdit, onDragStart, onDragOver, onDrop, dragOver }) {
  const weeks = [];
  for (let i = 0; i < range.days.length; i += 7) weeks.push(range.days.slice(i, i + 7));
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
      <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
        {["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"].map((d) => (
          <div key={d} className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">{d}</div>
        ))}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 border-b last:border-b-0 border-slate-200">
          {week.map((d) => {
            const key = fmt(d);
            const inMonth = d.getMonth() === cursor.getMonth();
            const isSunday = d.getDay() === 0;
            const isHoliday = FR_HOLIDAYS.has(key);
            const isToday = fmt(new Date()) === key;
            const items = byDay[key] || [];
            return (
              <div key={key}
                onDragOver={(e)=>onDragOver(e, key)} onDrop={(e)=>onDrop(e, key)}
                className={`border-r last:border-r-0 border-slate-200 min-h-[130px] p-1.5 transition-colors ${!inMonth?"bg-slate-50/60 opacity-60":isHoliday?"bg-amber-50":isSunday?"bg-indigo-50":""} ${dragOver===key?"bg-sky-50":""}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-bold ${isToday?"bg-sky-600 text-white rounded-full px-1.5":""}`}>{d.getDate()}</span>
                  <button onClick={()=>onCreate(key)} className="text-slate-400 hover:text-sky-600"><Plus className="h-3 w-3"/></button>
                </div>
                <div className="space-y-1">
                  <AbsLine list={absByDay[key]} k={key} />
                  {items.slice(0, 3).map((v) => (
                    <VacationCard key={v.id} v={v} site={sites.find(s=>s.id===v.site_id)} agent={agents.find(a=>a.id===v.agent_id)} compact onDragStart={onDragStart} onClick={()=>onEdit({...v})}/>
                  ))}
                  {items.length > 3 && <div className="text-[10px] text-slate-500 font-bold px-1">+{items.length - 3} autre(s)</div>}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function AgentsView({ absences, days, vacations, agents, sites, onCreate, onEdit, onDragStart, onDragOver, onDropOnAgent, dragOver }) {
  // Build matrix: agents rows + "non affecté" row, cols = 7 days
  const rows = useMemo(
    () => [{ id: null, label: "Non affecté" }, ...agents.map(a => ({ id: a.id, label: `${a.last_name} ${a.first_name}`, short: `${a.last_name.slice(0,6)}` }))],
    [agents]
  );
  const byAgentDay = useMemo(() => {
    const m = {};
    for (const r of rows) for (const d of days) m[`${r.id || "_"}|${fmt(d)}`] = [];
    for (const v of vacations) {
      const k = `${v.agent_id || "_"}|${v.date}`;
      if (m[k]) m[k].push(v);
    }
    for (const k of Object.keys(m)) m[k].sort((a,b) => a.start_time.localeCompare(b.start_time));
    return m;
  }, [rows, days, vacations]);
  const absAt = useMemo(() => {
    const m = {};
    absences.forEach((a) => a.days.forEach((d) => { m[`${a.agent_id}|${d}`] = a; }));
    return m;
  }, [absences]);

  return (
    <div className="border border-slate-200 rounded-md overflow-auto bg-white" data-testid="agents-view">
      <table className="w-full text-sm min-w-[1000px]">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-slate-500 w-48 sticky left-0 bg-slate-50">Agent</th>
            {days.map((d) => (
              <th key={fmt(d)} className={`px-3 py-2 text-[10px] uppercase tracking-widest font-bold ${d.getDay()===0?"text-indigo-700":"text-slate-500"}`}>
                {d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit" })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id || "_"} className="border-b border-slate-100 align-top">
              <td className={`px-3 py-2 font-semibold sticky left-0 bg-white ${r.id===null?"text-rose-700":""}`}>{r.label}</td>
              {days.map((d) => {
                const key = fmt(d);
                const slot = `${r.id || "_"}|${key}`;
                const items = byAgentDay[slot] || [];
                const isDragOver = dragOver === slot;
                return (
                  <td key={slot}
                    onDragOver={(e)=>onDragOver(e, slot)} onDrop={(e)=>onDropOnAgent(e, r.id, key)}
                    className={`px-1.5 py-1.5 min-w-[130px] transition-colors ${isDragOver?"bg-sky-50":""}`}
                    data-testid={`slot-${r.id || "unassigned"}-${key}`}>
                    <div className="space-y-1">
                      {r.id && absAt[slot] && <AbsChip a={{ ...absAt[slot], agent_name: "" }} testid={`agents-abs-${slot}`} />}
                      {items.map((v) => (
                        <VacationCard key={v.id} v={v} site={sites.find(s=>s.id===v.site_id)} agent={agents.find(a=>a.id===v.agent_id)} compact onDragStart={onDragStart} onClick={()=>onEdit({...v})}/>
                      ))}
                      <button onClick={()=>onCreate(key, r.id)} className="w-full border border-dashed border-slate-200 rounded text-[10px] py-1 text-slate-400 hover:border-sky-400 hover:text-sky-600">+</button>
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VacationModal({ value, onClose, onSaved, agents, sites }) {
  const { user } = useAuth();
  const isEdit = !!value.id;
  const [v, setV] = useState(value);
  const [subs, setSubs] = useState([]);
  useEffect(() => { http.get("/subcontractors").then((r) => setSubs(r.data)).catch(() => {}); }, []);
  const sortedAgents = sortForSite(agents, sites.find((s) => s.id === v.site_id), subs);
  useActivities();
  const actsFor = (cur) => siteActivities(sites.find((s) => s.id === v.site_id), cur);
  const [busy, setBusy] = useState({});
  useEffect(() => {
    if (!v.date || !/^\d{2}:\d{2}$/.test(v.start_time || "") || !/^\d{2}:\d{2}$/.test(v.end_time || "")) { setBusy({}); return; }
    const t = setTimeout(() => http.post("/planning/availability", { date: v.date, start_time: v.start_time, end_time: v.end_time, vacation_id: v.id || null }).then((r) => setBusy(r.data)).catch(() => setBusy({})), 300);
    return () => clearTimeout(t);
  }, [v.date, v.start_time, v.end_time, v.id]);
  // Liste des créneaux (pour mode "création multiple")
  const [slots, setSlots] = useState([{ activity: "", count: 1 }]);
  // Mode "répétition" : étendre la création sur plusieurs jours
  const [repeat, setRepeat] = useState(false);
  const [endDate, setEndDate] = useState("");
  const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4, 5, 6]); // 0=lun … 6=dim
  const [includeHolidays, setIncludeHolidays] = useState(true);
  const [repeatMode, setRepeatMode] = useState("range");
  const [pickedDates, setPickedDates] = useState([]);
  useEffect(() => { setPickedDates((p) => p.filter((d) => d.slice(0, 7) === (v.date || "").slice(0, 7) && d !== v.date)); }, [v.date]);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  // Calcule le nombre de jours qui matchent dans la plage (preview)
  const nbDays = (() => {
    if (repeat && repeatMode === "dates") return pickedDates.length + 1;
    if (!repeat || !v.date || !endDate) return 1;
    try {
      const start = new Date(v.date + "T00:00:00");
      const end = new Date(endDate + "T00:00:00");
      if (end < start) return 0;
      let n = 0;
      const wdSet = new Set(weekdays);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        // JS getDay : 0=dim ... 6=sam ; on transpose vers 0=lun ... 6=dim
        const wd = (d.getDay() + 6) % 7;
        if (!wdSet.has(wd)) continue;
        if (!includeHolidays) {
          const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
          if (FR_HOLIDAYS.has(iso)) continue;
        }
        n++;
      }
      return n;
    } catch { return 0; }
  })();

  const totalSlots = slots.reduce((sum, s) => sum + Math.max(0, parseInt(s.count) || 0), 0);
  const totalVacations = totalSlots * Math.max(1, nbDays);
  const isMulti = !isEdit && (repeat || slots.length > 1 || totalSlots > 1);

  const setSlot = (idx, key, val) => {
    setSlots((prev) => prev.map((s, i) => (i === idx ? { ...s, [key]: val } : s)));
  };
  const addSlot = () => setSlots((prev) => [...prev, { activity: "", count: 1 }]);
  const removeSlot = (idx) => setSlots((prev) => prev.filter((_, i) => i !== idx));

  const closedSite = sites.find((s) => s.id === v.site_id)?._closed;
  const save = async () => {
    if (closedSite && !window.confirm(`Le client ${closedSite.client_name} est fermé dans l'annuaire des entreprises.\n\nContinuer quand même ?`)) return;
    setSaving(true); setErr("");
    try {
      if (isEdit) {
        const body = {
          site_id: v.site_id, agent_id: v.agent_id || null,
          date: v.date, start_time: v.start_time, end_time: v.end_time,
          shift_type: v.shift_type || "regular",
          activity: v.activity || null,
          notes: v.notes || "",
          subcontractor_id: v.agent_id ? null : (v.subcontractor_id || null),
          min_floor: v.min_floor !== false,
          non_billable: !!v.non_billable, non_billable_reason: v.non_billable ? (v.non_billable_reason || "") : "",
        };
        await vacationCall("put", `/vacations/${v.id}`, body);
      } else if (isMulti) {
        const cleanSlots = slots
          .map((s) => ({ activity: s.activity || null, count: Math.max(0, parseInt(s.count) || 0) }))
          .filter((s) => s.count > 0);
        if (cleanSlots.length === 0) {
          throw new Error("Indiquez au moins un créneau avec une quantité > 0.");
        }
        const body = {
          site_id: v.site_id,
          date: v.date,
          start_time: v.start_time,
          end_time: v.end_time,
          shift_type: v.shift_type || "regular",
          notes: v.notes || "",
          slots: cleanSlots,
          min_floor: v.min_floor !== false,
          non_billable: !!v.non_billable, non_billable_reason: v.non_billable ? (v.non_billable_reason || "") : "",
        };
        if (repeat && repeatMode === "dates") {
          if (!pickedDates.length) throw new Error("Choisissez au moins une autre date dans le calendrier.");
          body.dates = pickedDates;
        } else if (repeat && endDate) {
          body.end_date = endDate;
          body.weekdays = weekdays;
          body.include_holidays = includeHolidays;
        }
        await http.post("/vacations/bulk", body);
      } else {
        // Création simple : 1 vacation, possiblement avec un agent
        const single = slots[0] || {};
        await vacationCall("post", "/vacations", {
          site_id: v.site_id,
          agent_id: v.agent_id || null,
          date: v.date, start_time: v.start_time, end_time: v.end_time,
          shift_type: v.shift_type || "regular",
          activity: single.activity || null,
          notes: v.notes || "",
          subcontractor_id: v.agent_id ? null : (v.subcontractor_id || null),
          min_floor: v.min_floor !== false,
          non_billable: !!v.non_billable, non_billable_reason: v.non_billable ? (v.non_billable_reason || "") : "",
        });
      }
      toast.success(isEdit ? "Vacation enregistrée" : "Vacation(s) créée(s)");
      onSaved(); onClose();
    } catch (e) {
      if (e.cancelled) return;
      if (e.requested) { toast.success(e.message); onClose(); return; }
      const msg = e?.response ? formatApiError(e) : (e?.message || String(e));
      setErr(msg);
    } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm("Supprimer cette vacation ?")) return;
    await http.delete(`/vacations/${v.id}`);
    onSaved(); onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-md w-full max-w-md border border-slate-200 animate-fade-in-up max-h-[95vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h3 className="font-display text-xl font-bold">{isEdit ? "Modifier la vacation" : (isMulti ? `Nouvelles vacations (${totalVacations})` : "Nouvelle vacation")}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-4 w-4"/></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Date"><DateInput data-testid="vacation-date" className="input" value={v.date} onChange={(e)=>setV({...v, date:e.target.value})}/></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Début"><input type="time" className="input" value={v.start_time} onChange={(e)=>setV({...v, start_time:e.target.value})}/></Field>
            <Field label="Fin"><input type="time" className="input" value={v.end_time} onChange={(e)=>setV({...v, end_time:e.target.value})}/></Field>
          </div>
          <Field label="Site">
            <SearchSelect data-testid="vacation-site" className="input" value={v.site_id} onChange={(e)=>setV({...v, site_id:e.target.value})}>
              <option value="">— Choisir —</option>
              {sites.map((s)=> <option key={s.id} value={s.id}>{s.name}{s._closed ? " (client fermé)" : ""}</option>)}
            </SearchSelect>
          </Field>
          {closedSite && (
            <div data-testid="vacation-closed-client-banner" className="flex items-start gap-2 px-3 py-2 rounded-md border border-rose-300 bg-rose-50 text-rose-800 text-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span><b>Client fermé</b> : l&apos;établissement de {closedSite.client_name}{closedSite.registry_name ? ` (${closedSite.registry_name})` : ""} est fermé dans l&apos;annuaire des entreprises.</span>
            </div>
          )}

          {isEdit ? (
            <>
              <Field label="Agent affecté">
                <AgentSearch agents={sortedAgents} busy={busy} value={v.agent_id} onChange={(id)=>setV({...v, agent_id:id})} />
              </Field>
              {!v.agent_id && v.id && <ReplacementPicks vacationId={v.id} slot={{ date: v.date, start_time: v.start_time, end_time: v.end_time, site_id: v.site_id, activity: v.activity || "" }} onPick={(a) => setV({ ...v, agent_id: a.id })} />}
              {!v.agent_id && subs.length > 0 && !user?.modules_off?.includes("sous_traitance") && <SubPick subs={subs} v={v} setV={setV} />}
              <Field label="Activité / Qualification requise">
                <select data-testid="vacation-activity" className="input" value={v.activity || ""} onChange={(e)=>setV({...v, activity:e.target.value})}>
                  <option value="">— Non précisé —</option>
                  {actsFor(v.activity).map((a)=> <option key={a.code} value={a.code}>{a.label}</option>)}
                </select>
                <NewActivityButton testid="vacation-new-activity" className="mt-1" onCreated={(c) => setV((x) => ({ ...x, activity: c }))} />
              </Field>
            </>
          ) : (
            <div className="border border-slate-200 rounded-md p-3 bg-slate-50/60 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600">Créneaux à créer</div>
                <span className="text-xs font-bold text-sky-700">
                  {totalSlots} vacation{totalSlots > 1 ? "s" : ""}
                </span>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Précisez l&apos;activité requise et le nombre d&apos;agents nécessaires. Les vacations seront créées <b>non affectées</b> et pourront être assignées ensuite via drag &amp; drop.
              </p>
              <div className="space-y-2">
                {slots.map((s, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`slot-row-${idx}`}>
                    <select
                      data-testid={`slot-activity-${idx}`}
                      className="input flex-1"
                      value={s.activity || ""}
                      onChange={(e) => setSlot(idx, "activity", e.target.value)}
                    >
                      <option value="">— Non précisé —</option>
                      {actsFor(s.activity).map((a) => (<option key={a.code} value={a.code}>{a.label}</option>))}
                    </select>
                    <input
                      data-testid={`slot-count-${idx}`}
                      type="number"
                      min="1"
                      max="50"
                      className="input text-center"
                      style={{ width: 80, flex: "none" }}
                      value={s.count}
                      onChange={(e) => setSlot(idx, "count", e.target.value)}
                      title="Nombre d'agents requis"
                    />
                    {slots.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSlot(idx)}
                        className="p-2 rounded hover:bg-rose-50 text-rose-600"
                        title="Retirer ce créneau"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                type="button"
                data-testid="add-slot"
                onClick={addSlot}
                className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-sky-700 hover:text-sky-900"
              >
                <Plus className="h-3.5 w-3.5" /> Ajouter une activité / un créneau
              </button>
              <NewActivityButton testid="slot-new-activity" className="ml-4" onCreated={(c) => setSlot(slots.length - 1, "activity", c)} />

              {/* Répétition sur plusieurs jours */}
              <div className="border-t border-slate-200 pt-3 mt-1">
                <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-600 cursor-pointer">
                  <input
                    data-testid="repeat-toggle"
                    type="checkbox"
                    checked={repeat}
                    onChange={(e) => {
                      setRepeat(e.target.checked);
                      if (e.target.checked && !endDate && v.date) {
                        // Pré-remplit avec la fin du mois courant
                        const d = new Date(v.date + "T00:00:00");
                        const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
                        const yy = last.getFullYear();
                        const mm = String(last.getMonth() + 1).padStart(2, "0");
                        const dd = String(last.getDate()).padStart(2, "0");
                        setEndDate(`${yy}-${mm}-${dd}`);
                      }
                    }}
                    className="h-4 w-4 accent-sky-600"
                  />
                  Répéter sur plusieurs jours
                </label>
                {repeat && (
                  <div className="mt-3 flex gap-1.5" data-testid="repeat-mode">
                    {[["range", "Période"], ["dates", "Dates précises"]].map(([k, l]) => (
                      <button key={k} type="button" data-testid={`repeat-mode-${k}`} onClick={() => setRepeatMode(k)}
                        className={`px-2.5 py-1 rounded-md text-xs font-bold border transition-colors ${repeatMode === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>{l}</button>
                    ))}
                  </div>
                )}
                {repeat && repeatMode === "dates" && (
                  <MonthDatesPicker base={v.date} value={pickedDates} onChange={setPickedDates} perDay={totalSlots} />
                )}
                {repeat && repeatMode === "range" && (
                  <div className="mt-3 space-y-3">
                    <Field label="Jusqu'au">
                      <DateInput
                        data-testid="repeat-end-date"
                        className="input"
                        value={endDate}
                        min={v.date}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </Field>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Jours de la semaine</div>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          { v: 0, l: "Lun" }, { v: 1, l: "Mar" }, { v: 2, l: "Mer" },
                          { v: 3, l: "Jeu" }, { v: 4, l: "Ven" }, { v: 5, l: "Sam" }, { v: 6, l: "Dim" },
                        ].map((d) => {
                          const active = weekdays.includes(d.v);
                          return (
                            <button
                              key={d.v}
                              type="button"
                              data-testid={`weekday-${d.v}`}
                              onClick={() => setWeekdays((prev) => active ? prev.filter((x) => x !== d.v) : [...prev, d.v])}
                              className={`px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-widest border ${active ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-300 hover:border-sky-400"}`}
                            >
                              {d.l}
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex gap-2 mt-2 text-[10px] font-bold uppercase tracking-widest">
                        <button type="button" onClick={() => setWeekdays([0,1,2,3,4])} className="text-sky-700 hover:underline">Sem. (lun-ven)</button>
                        <span className="text-slate-300">·</span>
                        <button type="button" onClick={() => setWeekdays([0,1,2,3,4,5,6])} className="text-sky-700 hover:underline">Tous</button>
                        <span className="text-slate-300">·</span>
                        <button type="button" onClick={() => setWeekdays([5,6])} className="text-sky-700 hover:underline">Week-end</button>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                      <input
                        data-testid="include-holidays"
                        type="checkbox"
                        checked={includeHolidays}
                        onChange={(e) => setIncludeHolidays(e.target.checked)}
                        className="h-4 w-4 accent-sky-600"
                      />
                      Inclure les jours fériés (sinon ils sont sautés)
                    </label>
                    {endDate && (
                      <div className="text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded px-2.5 py-1.5">
                        {nbDays > 0
                          ? <>{nbDays} jour{nbDays > 1 ? "s" : ""} × {totalSlots} vacation{totalSlots > 1 ? "s" : ""}/jour = <b>{totalVacations} vacations</b> au total.</>
                          : "Aucune date ne correspond aux jours sélectionnés."}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {!isMulti && (
                <Field label="Agent affecté (optionnel)">
                  <AgentSearch agents={sortedAgents} busy={busy} value={v.agent_id} onChange={(id)=>setV({...v, agent_id:id})} />
                </Field>
              )}
              {!isMulti && !v.agent_id && subs.length > 0 && !user?.modules_off?.includes("sous_traitance") && <SubPick subs={subs} v={v} setV={setV} />}
              {isMulti && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  Mode multiple — toutes les vacations seront créées non affectées.
                </div>
              )}
            </div>
          )}

          <Field label="Notes"><textarea className="input" rows={2} value={v.notes} onChange={(e)=>setV({...v, notes:e.target.value})}/></Field>
          <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="vacation-min-floor-label">
            <input type="checkbox" className="mt-0.5 h-4 w-4" checked={v.min_floor !== false} onChange={(e)=>setV({...v, min_floor:e.target.checked})} data-testid="vacation-min-floor" />
            <span><b>Appliquer le plancher minimum par vacation</b> <span className="text-xs text-slate-500">(IDCC 1351, 6 h par défaut, réglable dans Paramètres) — une vacation plus courte est facturée et payée sur ce minimum</span></span>
          </label>
          <NonBillableField v={v} setV={setV} />
          {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
        </div>
        <div className="flex items-center justify-between px-5 py-4 border-t border-slate-200 bg-slate-50 sticky bottom-0">
          {isEdit ? (
            <button data-testid="vacation-delete" onClick={remove} className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-700 text-sm font-semibold"><Trash2 className="h-4 w-4"/> Supprimer</button>
          ) : <span/>}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-md hover:bg-slate-100">Annuler</button>
            <button data-testid="vacation-save" disabled={saving} onClick={save} className="px-4 py-2 text-sm font-semibold bg-sky-600 hover:bg-sky-700 text-white rounded-md disabled:opacity-70">
              {isMulti ? `Créer ${totalVacations} vacations` : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid #cbd5e1;border-radius:6px;padding:.55rem .7rem;font-size:.875rem;outline:none;background:white}.input:focus{border-color:#0284c7;box-shadow:0 0 0 3px #0ea5e933}`}</style>
    </div>
  );
}

function SubPick({ subs, v, setV }) {
  return (
    <Field label="Ou confier à un sous-traitant (agent à désigner)">
      <SearchSelect className="input" value={v.subcontractor_id || ""} onChange={(e) => setV({ ...v, subcontractor_id: e.target.value || null })} data-testid="vacation-subcontractor">
        <option value="">—</option>{subs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </SearchSelect>
    </Field>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function GenerateDevisModal({ range, view, cursor, clients, sites, filterClient, filterSite, visibleCount, onClose, onCreated }) {
  const [start, setStart] = useState(() => {
    if (view === "month") return fmt(startOfMonth(cursor));
    return fmt(range.start);
  });
  const [end, setEnd] = useState(() => {
    if (view === "month") return fmt(endOfMonth(cursor));
    return fmt(range.end);
  });
  const [clientId, setClientId] = useState(filterClient || "");
  const [siteId, setSiteId] = useState(filterSite || "");
  const [assignmentFilter, setAssignmentFilter] = useState("all");
  const [groupMode, setGroupMode] = useState("activity"); // 'activity' | 'day' | 'full'
  const [applyFloor, setApplyFloor] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const filteredSites = clientId ? sites.filter((s) => s.client_id === clientId) : sites;
  useEffect(() => {
    if (clientId && siteId && !filteredSites.some((s) => s.id === siteId)) setSiteId("");
  }, [clientId, siteId, filteredSites]);

  const submit = async () => {
    setErr(""); setSubmitting(true);
    try {
      const body = {
        start, end,
        site_id: siteId || null,
        client_id: clientId || null,
        assignment_filter: assignmentFilter,
        group_mode: groupMode,
        apply_min_floor: applyFloor,
      };
      const r = await http.post("/vacations/generate-devis", body);
      onCreated && onCreated(r.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-md w-full max-w-lg border border-slate-200 animate-fade-in-up max-h-[95vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-display text-xl font-bold">Générer un devis depuis le planning</h3>
            <p className="text-xs text-slate-500 mt-0.5">Le devis est créé en brouillon dans l&apos;onglet Devis.</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date début"><DateInput data-testid="gen-devis-start" className="input" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
            <Field label="Date fin"><DateInput data-testid="gen-devis-end" className="input" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
          </div>
          <Field label="Client (rattachement du devis)">
            <SearchSelect data-testid="gd-client" className="input" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">— Aucun (devis sans rattachement client) —</option>
              {clients.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </SearchSelect>
          </Field>
          <Field label="Site (optionnel)">
            <SearchSelect data-testid="gd-site" className="input" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">{clientId ? `Tous les sites du client (${filteredSites.length})` : "Tous les sites"}</option>
              {filteredSites.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
            </SearchSelect>
          </Field>

          <div className="border border-slate-200 rounded-md p-3 bg-slate-50/60">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">Vacations à inclure</div>
            <div className="space-y-2">
              {[
                { v: "all", label: "Toutes les vacations", desc: "Affectées + non affectées" },
                { v: "assigned", label: "Affectées uniquement", desc: "Uniquement celles avec un agent assigné" },
                { v: "unassigned", label: "Non affectées uniquement", desc: "Pour facturer les besoins non couverts" },
              ].map((opt) => (
                <label key={opt.v} className={`flex items-start gap-2 p-2 rounded cursor-pointer border ${assignmentFilter === opt.v ? "border-sky-500 bg-sky-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                  <input
                    type="radio"
                    data-testid={`gd-filter-${opt.v}`}
                    name="af"
                    value={opt.v}
                    checked={assignmentFilter === opt.v}
                    onChange={(e) => setAssignmentFilter(e.target.value)}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-800">{opt.label}</div>
                    <div className="text-xs text-slate-500">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="border border-slate-200 rounded-md p-3 bg-slate-50/60">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">Niveau de détail des lignes</div>
            <div className="space-y-2">
              {[
                { v: "activity", label: "Par activité", desc: "1 ligne par activité + type d'heure (recommandé)" },
                { v: "day", label: "Par jour", desc: "1 ligne par date × activité × type d'heure" },
                { v: "full", label: "Détail complet", desc: "1 ligne par vacation individuelle" },
              ].map((opt) => (
                <label key={opt.v} className={`flex items-start gap-2 p-2 rounded cursor-pointer border ${groupMode === opt.v ? "border-sky-500 bg-sky-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                  <input
                    type="radio"
                    data-testid={`gd-mode-${opt.v}`}
                    name="gm"
                    value={opt.v}
                    checked={groupMode === opt.v}
                    onChange={(e) => setGroupMode(e.target.value)}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-800">{opt.label}</div>
                    <div className="text-xs text-slate-500">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="mt-0.5 h-4 w-4" checked={applyFloor} onChange={(e) => setApplyFloor(e.target.checked)} data-testid="planning-gen-min-floor" />
            <span>Appliquer le plancher minimum par vacation <span className="text-xs text-slate-500">(IDCC 1351, durée réglée dans Paramètres — sinon facturation au temps réel, avec mention sur le devis)</span></span>
          </label>

          {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
          <div className="text-xs text-slate-500">{visibleCount} vacation{visibleCount > 1 ? "s" : ""} actuellement affichée{visibleCount > 1 ? "s" : ""} dans le planning. Le devis utilisera les filtres ci-dessus.</div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50 sticky bottom-0">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-md hover:bg-slate-100">Annuler</button>
          <button data-testid="gd-submit" disabled={submitting} onClick={submit} className="px-4 py-2 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-md disabled:opacity-70">
            {submitting ? "Génération…" : "Générer le devis"}
          </button>
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid #cbd5e1;border-radius:6px;padding:.55rem .7rem;font-size:.875rem;outline:none;background:white}.input:focus{border-color:#0284c7;box-shadow:0 0 0 3px #0ea5e933}`}</style>
    </div>
  );
}
